import { useEffect, useMemo, useRef, useState } from 'react';
import BizInput from '../Mini/BizInput';
import { BizButton } from '../Mini/BizButton';
import BizTextArea from '../Mini/BizTextArea';
import { useGlobalState } from '../../Contexts/globalStateContext';
import { saveCheckInResponsesOnUnload, useGetResolvedCheckIns, useSaveCheckInResponses } from '../../Services/checkIn';
import { ObjectType } from '../../Types/base';
import {
	CheckInInputType,
	CheckInResponse,
	SaveCheckInResponsesRequest,
	DEFAULT_CHECKIN_RATING_OPTION_COUNT,
	ResolvedCheckIn,
} from '../../Types/admin/checkIn';
import { useTranslation } from 'react-i18next';
import './CheckInForm.scss';

const AUTO_SAVE_DELAY = 2000;
const OTHERS_GROUP_KEY = 'others';

type CheckInFormProps = {
	objectType: ObjectType;
	objectId: number;
	title?: string;
	hideTitle?: boolean;
	hideDisabled?: boolean;
	saveMode?: 'auto' | 'manual';
};

type SubjectCheckInGroup = {
	subjectUserId: number;
	subjectUserName: string;
	actionableItems: ResolvedCheckIn[];
	secondaryItems: ResolvedCheckIn[];
};

type RelatedUserGroup = {
	groupKey: string;
	relatedUserId?: number;
	relatedUserName: string;
	subjectGroups: SubjectCheckInGroup[];
	defaultExpanded: boolean;
};

const getQuestionText = (checkIn: ResolvedCheckIn, fallbackQuestionText: string, cultureId?: number) => {
	const preferredTranslation = checkIn.questionTranslations.find(
		(translation) => translation.cultureId === cultureId && translation.value.trim().length > 0
	);
	if (preferredTranslation) {
		return preferredTranslation.value;
	}

	return checkIn.questionTranslations.find((translation) => translation.value.trim().length > 0)?.value ?? fallbackQuestionText;
};

const getResponseKey = (checkIn: ResolvedCheckIn) => `${checkIn.checkInDefinitionId}_${checkIn.subjectUserId}`;

const getRatingOptionCount = (checkIn: ResolvedCheckIn) => {
	return checkIn.ratingOptionCount > 0 ? checkIn.ratingOptionCount : DEFAULT_CHECKIN_RATING_OPTION_COUNT;
};

const getRatingOptions = (checkIn: ResolvedCheckIn) => {
	return Array.from({ length: getRatingOptionCount(checkIn) }, (_, index) => String(index + 1));
};

const isUserSubject = (checkIn: ResolvedCheckIn, userId?: number) => {
	return checkIn.subjectUserId === userId;
};

const isUserRespondent = (checkIn: ResolvedCheckIn, userId?: number) => {
	return checkIn.respondentUserId === userId;
};

const canUserRespondToCheckIn = (checkIn: ResolvedCheckIn, userId?: number) => {
	return checkIn.isEnabled && isUserRespondent(checkIn, userId);
};

const sortCheckIns = (items: ResolvedCheckIn[]) =>
	[...items].sort((left, right) => left.order - right.order || left.checkInDefinitionId - right.checkInDefinitionId);

const getFirstRelatedUser = (group: SubjectCheckInGroup) => {
	const checkIns = [...group.actionableItems, ...group.secondaryItems];

	for (const checkIn of checkIns) {
		for (const relation of checkIn.subjectUserRelations) {
			if (relation.parentUserId === group.subjectUserId) {
				return {
					relatedUserId: relation.childUserId,
					relatedUserName: relation.childUserDisplayName,
				};
			}

			if (relation.childUserId === group.subjectUserId) {
				return {
					relatedUserId: relation.parentUserId,
					relatedUserName: relation.parentUserDisplayName,
				};
			}
		}
	}

	return null;
};

const CheckInForm = ({
	objectType,
	objectId,
	title,
	hideTitle = false,
	hideDisabled = false,
	saveMode = 'auto',
}: CheckInFormProps) => {
	const { t } = useTranslation();
	const {
		state: { cultureId, legacy, user },
	} = useGlobalState();
	const { data: resolvedCheckIns } = useGetResolvedCheckIns(objectType, objectId);
	const saveMutation = useSaveCheckInResponses();
	const [values, setValues] = useState<Record<string, string>>({});
	const [expandedSubjects, setExpandedSubjects] = useState<Record<number, boolean>>({});
	const [expandedRelatedUsers, setExpandedRelatedUsers] = useState<Record<string, boolean>>({});
	const protectedValuesRef = useRef<Record<string, string>>({});
	const saveTimeoutsRef = useRef<Record<string, number>>({});
	const pendingRequestsRef = useRef<Record<string, SaveCheckInResponsesRequest>>({});

	const clearScheduledSave = (responseKey: string) => {
		const timeoutId = saveTimeoutsRef.current[responseKey];

		if (timeoutId !== undefined) {
			window.clearTimeout(timeoutId);
			delete saveTimeoutsRef.current[responseKey];
		}
	};

	const clearPendingSave = (responseKey: string) => {
		clearScheduledSave(responseKey);
		delete pendingRequestsRef.current[responseKey];
	};

	const flushPendingSavesOnUnload = () => {
		const pendingRequests = Object.values(pendingRequestsRef.current);

		if (pendingRequests.length === 0) {
			return;
		}

		void saveCheckInResponsesOnUnload({
			objectType,
			objectId,
			responses: pendingRequests.flatMap((request) => request.responses),
		});

		Object.keys(pendingRequestsRef.current).forEach((responseKey) => {
			clearScheduledSave(responseKey);
		});
		pendingRequestsRef.current = {};
	};

	useEffect(() => {
		const handlePageHide = () => {
			flushPendingSavesOnUnload();
		};

		window.addEventListener('pagehide', handlePageHide);

		return () => {
			window.removeEventListener('pagehide', handlePageHide);
			flushPendingSavesOnUnload();
		};
	}, [objectId, objectType]);

	useEffect(() => {
		if (!resolvedCheckIns) {
			return;
		}

		setValues((current) => {
			const nextValues = { ...current };

			resolvedCheckIns.forEach((checkIn) => {
				const responseKey = getResponseKey(checkIn);
				const backendValue = checkIn.value ?? '';
				const protectedValue = saveMode === 'auto' ? protectedValuesRef.current[responseKey] : undefined;

				if (protectedValue !== undefined && protectedValue !== backendValue) {
					nextValues[responseKey] = current[responseKey] ?? protectedValue;
					return;
				}

				if (protectedValue === backendValue) {
					delete protectedValuesRef.current[responseKey];
				}

				nextValues[responseKey] = backendValue;
			});

			return nextValues;
		});
	}, [resolvedCheckIns, saveMode]);

	const displayedCheckIns = useMemo(() => {
		const checkIns = resolvedCheckIns ?? [];

		if (!hideDisabled) {
			return checkIns;
		}

		return checkIns.filter((checkIn) => checkIn.isEnabled);
	}, [hideDisabled, resolvedCheckIns]);

	const hideSubject = displayedCheckIns.every((checkIn) => isUserSubject(checkIn, user?.Id));

	const groupedCheckIns = useMemo(() => {
		const groups = new Map<number, SubjectCheckInGroup>();

		displayedCheckIns.forEach((checkIn) => {
			const existingGroup = groups.get(checkIn.subjectUserId);
			const groupKey = isUserRespondent(checkIn, user?.Id) ? 'actionableItems' : 'secondaryItems';
			if (existingGroup) {
				existingGroup[groupKey].push(checkIn);
				return;
			}

			groups.set(checkIn.subjectUserId, {
				subjectUserId: checkIn.subjectUserId,
				subjectUserName: checkIn.subjectUserName,
				actionableItems: groupKey === 'actionableItems' ? [checkIn] : [],
				secondaryItems: groupKey === 'secondaryItems' ? [checkIn] : [],
			});
		});

		return Array.from(groups.values()).map((group) => ({
			...group,
			actionableItems: sortCheckIns(group.actionableItems),
			secondaryItems: sortCheckIns(group.secondaryItems),
		}));
	}, [displayedCheckIns, user?.Id]);

	const othersGroupText = t('CHECKIN.OTHERS_GROUP', { defaultValue: 'Others' });

	const relatedUserGroups = useMemo(() => {
		if (groupedCheckIns.length <= 1) {
			return [];
		}

		const groups = new Map<string, RelatedUserGroup>();

		groupedCheckIns.forEach((group) => {
			const relatedUser = getFirstRelatedUser(group);
			const groupKey = relatedUser ? String(relatedUser.relatedUserId) : OTHERS_GROUP_KEY;
			const relatedUserName = relatedUser?.relatedUserName ?? othersGroupText;
			const relatedUserId = relatedUser?.relatedUserId;
			const existingGroup = groups.get(groupKey);

			if (existingGroup) {
				existingGroup.subjectGroups.push(group);
				return;
			}

			groups.set(groupKey, {
				groupKey,
				relatedUserId,
				relatedUserName,
				subjectGroups: [group],
				defaultExpanded: relatedUserId === user?.Id,
			});
		});

		return Array.from(groups.values()).sort((left, right) => {
			if (left.groupKey === OTHERS_GROUP_KEY && right.groupKey !== OTHERS_GROUP_KEY) {
				return 1;
			}

			if (right.groupKey === OTHERS_GROUP_KEY && left.groupKey !== OTHERS_GROUP_KEY) {
				return -1;
			}

			if (left.relatedUserId === user?.Id && right.relatedUserId !== user?.Id) {
				return -1;
			}

			if (right.relatedUserId === user?.Id && left.relatedUserId !== user?.Id) {
				return 1;
			}

			return 0;
		});
	}, [groupedCheckIns, user?.Id, othersGroupText]);

	const shouldGroupByRelatedUser = groupedCheckIns.length > 1 && relatedUserGroups.length > 0;
	const defaultTitle = t('CHECKIN.TITLE', { defaultValue: 'Check-In' });
	const fallbackQuestionText = t('CHECKIN.QUESTION_FALLBACK', { defaultValue: 'Check-in question' });
	const otherCheckInsText = t('CHECKIN.OTHER_CHECKINS', { defaultValue: 'Other check-ins' });
	const saveResponsesText = t('CHECKIN.SAVE_RESPONSES', { defaultValue: 'Save responses' });

	const toggleSubjectExpanded = (subjectUserId: number) => {
		setExpandedSubjects((current) => ({
			...current,
			[subjectUserId]: !current[subjectUserId],
		}));
	};

	const toggleRelatedUserExpanded = (groupKey: string) => {
		setExpandedRelatedUsers((current) => ({
			...current,
			[groupKey]: !(
				current[groupKey] ??
				relatedUserGroups.find((group) => group.groupKey === groupKey)?.defaultExpanded ??
				false
			),
		}));
	};

	const updateValue = (checkIn: ResolvedCheckIn, value: string) => {
		const responseKey = getResponseKey(checkIn);
		setValues((current) => ({
			...current,
			[responseKey]: value,
		}));

		if (saveMode !== 'auto' || !canUserRespondToCheckIn(checkIn, user?.Id)) {
			return;
		}

		protectedValuesRef.current[responseKey] = value;
	};

	const saveResponse = async (checkIn: ResolvedCheckIn, value: string) => {
		if (!canUserRespondToCheckIn(checkIn, user?.Id)) {
			return;
		}

		const responseKey = getResponseKey(checkIn);

		await saveMutation.mutateAsync({
			objectType,
			objectId,
			responses: [
				{
					checkInDefinitionId: checkIn.checkInDefinitionId,
					objectType: checkIn.objectType,
					objectId: checkIn.objectId,
					subjectUserId: checkIn.subjectUserId,
					respondentUserId: checkIn.respondentUserId,
					value,
				},
			],
		});

		clearPendingSave(responseKey);
	};

	const scheduleSaveResponse = (checkIn: ResolvedCheckIn, value: string) => {
		const responseKey = getResponseKey(checkIn);
		clearScheduledSave(responseKey);
		pendingRequestsRef.current[responseKey] = {
			objectType,
			objectId,
			responses: [
				{
					checkInDefinitionId: checkIn.checkInDefinitionId,
					objectType: checkIn.objectType,
					objectId: checkIn.objectId,
					subjectUserId: checkIn.subjectUserId,
					respondentUserId: checkIn.respondentUserId,
					value,
				},
			],
		};

		saveTimeoutsRef.current[responseKey] = window.setTimeout(() => {
			delete saveTimeoutsRef.current[responseKey];
			void saveResponse(checkIn, value);
		}, AUTO_SAVE_DELAY);
	};

	const renderCheckInInput = (checkIn: ResolvedCheckIn) => {
		const isEditable = canUserRespondToCheckIn(checkIn, user?.Id);
		const responseKey = getResponseKey(checkIn);
		const value = values[responseKey] ?? '';
		const shouldAutoSave = saveMode === 'auto' && isEditable;

		if (checkIn.type === CheckInInputType.TextArea) {
			if (shouldAutoSave) {
				return (
					<BizTextArea
						rows={4}
						value={value}
						onChange={(event) => {
							const newValue = event.target.value;
							updateValue(checkIn, newValue);
							scheduleSaveResponse(checkIn, newValue);
						}}
						readonly={!isEditable}
					/>
				);
			}

			return (
				<BizTextArea
					rows={4}
					value={value}
					onChange={(event) => updateValue(checkIn, event.target.value)}
					readonly={!isEditable}
				/>
			);
		}

		if (checkIn.type === CheckInInputType.Rating) {
			const selectedValue = value;
			return (
				<div className='biz-check-in-form__rating-options'>
					{getRatingOptions(checkIn).map((ratingValue) => (
						<button
							type='button'
							key={ratingValue}
							className={`biz-check-in-form__rating-option ${selectedValue === ratingValue ? 'selected' : ''}`}
							onClick={() => {
								updateValue(checkIn, ratingValue);
								if (shouldAutoSave) {
									void saveResponse(checkIn, ratingValue);
								}
							}}
							disabled={!isEditable}
							aria-pressed={selectedValue === ratingValue}
						>
							<span>{ratingValue}</span>
						</button>
					))}
				</div>
			);
		}

		if (shouldAutoSave) {
			return (
				<BizInput
					type={checkIn.type === CheckInInputType.Number ? 'number' : 'text'}
					value={value}
					onChange={(event) => {
						const newValue = event.target.value;
						updateValue(checkIn, newValue);
						scheduleSaveResponse(checkIn, newValue);
					}}
					readonly={!isEditable}
				/>
			);
		}

		return (
			<BizInput
				type={checkIn.type === CheckInInputType.Number ? 'number' : 'text'}
				value={value}
				onChange={(event) => updateValue(checkIn, event.target.value)}
				readonly={!isEditable}
			/>
		);
	};

	const saveResponses = async () => {
		if (!resolvedCheckIns) {
			return;
		}

		const responses: CheckInResponse[] = resolvedCheckIns
			.filter((ci) => canUserRespondToCheckIn(ci, user?.Id))
			.map((checkIn) => ({
				checkInDefinitionId: checkIn.checkInDefinitionId,
				objectType: checkIn.objectType,
				objectId: checkIn.objectId,
				subjectUserId: checkIn.subjectUserId,
				respondentUserId: checkIn.respondentUserId,
				value: values[getResponseKey(checkIn)] ?? '',
			}));

		await saveMutation.mutateAsync({
			objectType,
			objectId,
			responses,
		});
	};

	if (displayedCheckIns.length === 0) {
		return null; //for now a hidden module that shows up if there are anything to show.
	}
	// if (statusState === 'LOADING') {
	// 	return <div className='biz-check-in-form'>Loading check-ins...</div>;
	// }

	// if (!resolvedCheckIns || resolvedCheckIns.length === 0) {
	// 	return <div className='biz-check-in-form biz-check-in-form--empty'>No check-ins available.</div>;
	// }

	const renderSubjectGroup = (group: SubjectCheckInGroup) => {
		const hasSecondaryItems = group.secondaryItems.length > 0;
		const isExpanded = expandedSubjects[group.subjectUserId] ?? false;

		return (
			<div className='biz-check-in-form__subject-group' key={group.subjectUserId}>
				{!hideSubject && <div className='biz-check-in-form__group-title'>{group.subjectUserName}</div>}
				<div className='biz-check-in-form__items'>
					{group.actionableItems.map((checkIn) => (
						<div
							className={`biz-check-in-form__item ${!canUserRespondToCheckIn(checkIn, user?.Id) ? 'readonly' : ''}`}
							key={getResponseKey(checkIn)}
						>
							<label className='control-label'>{getQuestionText(checkIn, fallbackQuestionText, cultureId)}</label>
							{renderCheckInInput(checkIn)}
						</div>
					))}
				</div>
				{hasSecondaryItems && (
					<div className='biz-check-in-form__secondary'>
						<button
							type='button'
							className='biz-check-in-form__secondary-toggle'
							onClick={() => toggleSubjectExpanded(group.subjectUserId)}
							aria-expanded={isExpanded}
						>
							<i className={`far ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} aria-hidden='true' />
							<span>{otherCheckInsText}</span>
						</button>
						{isExpanded && (
							<div className='biz-check-in-form__items biz-check-in-form__items--secondary'>
								{group.secondaryItems.map((checkIn) => (
									<div
										className={`biz-check-in-form__item ${!canUserRespondToCheckIn(checkIn, user?.Id) ? 'readonly' : ''}`}
										key={getResponseKey(checkIn)}
									>
										<label className='control-label'>{getQuestionText(checkIn, fallbackQuestionText, cultureId)}</label>
										{renderCheckInInput(checkIn)}
									</div>
								))}
							</div>
						)}
					</div>
				)}
			</div>
		);
	};

	return (
		<div className='biz-check-in-form'>
			{!hideTitle && (
				<div className='biz-check-in-form__header'>
					<h3>{title || defaultTitle}</h3>
				</div>
			)}

			<div className='biz-check-in-form__groups'>
				{shouldGroupByRelatedUser
					? relatedUserGroups.map((group) => {
							const isExpanded = expandedRelatedUsers[group.groupKey] ?? group.defaultExpanded;

							return (
								<div
									className='biz-check-in-form__group biz-check-in-form__group--related-user'
									key={group.groupKey}
								>
									<button
										type='button'
										className='biz-check-in-form__secondary-toggle biz-check-in-form__related-user-toggle'
										onClick={() => toggleRelatedUserExpanded(group.groupKey)}
										aria-expanded={isExpanded}
									>
										<i
											className={`far ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`}
											aria-hidden='true'
										/>
										<span>{group.relatedUserName}</span>
									</button>
									{isExpanded && (
										<div className='biz-check-in-form__related-user-subjects'>
											{group.subjectGroups.map(renderSubjectGroup)}
										</div>
									)}
								</div>
							);
						})
					: groupedCheckIns.map((group) => (
							<div className='biz-check-in-form__group' key={group.subjectUserId}>
								{renderSubjectGroup(group)}
							</div>
						))}
			</div>

			{saveMode === 'manual' && (
				<div className='biz-check-in-form__actions'>
					<BizButton
						text={saveResponsesText}
						onClick={saveResponses}
						icon='far fa-save'
						isLead={true}
						state={saveMutation.statusState}
						legacy={legacy}
					/>
				</div>
			)}
		</div>
	);
};

export default CheckInForm;
